import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import GeneralSettingsTab from './components/GeneralSettingsTab';
import AssetCategoriesTab from './components/AssetCategoriesTab';
import SoftwareCategoryTab from './components/SoftwareCategoryTab';
import AuditConfigTab from './components/AuditConfigTab';
import IntegrationTab from './components/IntegrationTab';
import NotificationRulesTab from './components/NotificationRulesTab';

const SystemConfigurationPanel = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  const tabs = [
    {
      id: 'general',
      label: 'General Settings',
      icon: 'Settings',
      description: 'Organization and system preferences'
    },
    {
      id: 'categories',
      label: 'Asset Categories',
      icon: 'Package',
      description: 'Manage asset types and custom fields'
    },
    {
      id: 'software_categories',
      label: 'Software Categories',
      icon: 'Layers',
      description: 'Software classifications and license templates'
    },
    {
      id: 'audit_config',
      label: 'Audit Configuration',
      icon: 'CheckSquare',
      description: 'Compliance standards, backup intervals, and audit policy'
    },
    {
      id: 'integrations',
      label: 'Integrations',
      icon: 'Zap',
      description: 'External system connections and webhooks'
    },
    {
      id: 'notifications',
      label: 'Notification Rules',
      icon: 'Bell',
      description: 'Automated alert rules, channel routing, and recipient triggers'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralSettingsTab />;
      case 'categories':
        return <AssetCategoriesTab />;
      case 'software_categories':
        return <SoftwareCategoryTab />;
      case 'audit_config':
        return <AuditConfigTab />;
      case 'integrations':
        return <IntegrationTab />;
      case 'notifications':
        return <NotificationRulesTab />;
      default:
        return <GeneralSettingsTab />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>System Configuration Panel - AssetTracker Pro</title>
        <meta name="description" content="Administrative control center for system-wide settings and integration management" />
      </Helmet>
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'} mt-16 min-h-screen`}>
        <div className="p-8">
          {/* Breadcrumb */}
          <Breadcrumb />

          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">System Configuration Panel</h1>
              <p className="text-muted-foreground mt-2">
                Administrative control center for system-wide settings and integration management
              </p>
            </div>
          </div>

          {/* Maintenance Mode Alert */}
          {maintenanceMode && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-3">
                <Icon name="AlertTriangle" size={20} className="text-error" />
                <div>
                  <h3 className="font-semibold text-error">Emergency Maintenance Mode Active</h3>
                  <p className="text-sm text-muted-foreground">
                    System is currently in maintenance mode. Only administrators can access the system.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setMaintenanceMode(false)}
                  className="ml-auto"
                >
                  Deactivate
                </Button>
              </div>
            </div>
          )}

          {/* Configuration Tabs */}
          <div className="bg-card rounded-lg border border-border shadow-subtle">
            {/* Tab Navigation */}
            <div className="border-b border-border overflow-x-auto">
              <nav className="flex space-x-6 px-6 min-w-max" aria-label="Configuration tabs">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-smooth ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {/* Tab Description */}
              <div className="mb-6">
                <p className="text-muted-foreground">
                  {tabs?.find(tab => tab?.id === activeTab)?.description}
                </p>
              </div>

              {/* Dynamic Tab Content */}
              {renderTabContent()}
            </div>
          </div>

          {/* Footer Information */}
          <div className="mt-8 p-6 bg-card rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Shield" size={16} />
                  <span>System Administrator Access Required</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Lock" size={16} />
                  <span>All changes are logged and audited</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Clock" size={16} />
                <span>© {new Date()?.getFullYear()} AssetTracker Pro. All rights reserved.</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SystemConfigurationPanel;